import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaShieldAlt, FaChartLine, FaGraduationCap, FaGlobeAmericas } from 'react-icons/fa';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    visible: { 
      transition: { 
        staggerChildren: 0.2 
      } 
    }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            About UPM Traders
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Your trusted partner in finding reliable trading brokers
          </motion.p>
        </div>
      </div>
      
      <div className="container">
        <motion.div 
          className="about-content"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerChildren}
          ref={ref}
        >
          <motion.div 
            className="about-text" 
            variants={fadeInUp}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Who We Are</h2>
            <p>
              UPM Traders is a comprehensive platform dedicated to helping traders find regulated and 
              registered brokers with instant withdrawal capabilities. Our mission is to provide a 
              trustworthy resource for traders seeking reliable platforms for their cryptocurrency 
              trading activities.
            </p>
            
            <p>
              In today's complex financial landscape, finding a trustworthy broker can be challenging. 
              With thousands of options available, traders need a reliable filter to identify brokers 
              that meet the highest standards of regulation, security, and service quality.
            </p>
          </motion.div>
          
          <motion.div 
            className="about-stats staggered-animations" 
            variants={staggerChildren}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="stat-card-about" 
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h3>250+</h3>
              <p>Regulated Brokers</p>
            </motion.div>
            <motion.div 
              className="stat-card-about" 
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h3>15K+</h3>
              <p>Active Traders</p>
            </motion.div>
            <motion.div 
              className="stat-card-about" 
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                borderColor: "#D4AF37",
                transition: { duration: 0.3 }
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3>98%</h3>
              <p>Client Satisfaction</p>
            </motion.div>
          </motion.div>
          
          <motion.div 
            className="mission" 
            variants={fadeInUp}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Our Mission</h2>
            <p>
              We aim to simplify the process of finding reputable brokers by thoroughly vetting and 
              listing only those that meet our strict criteria for regulation, registration, and 
              withdrawal efficiency. Our team continuously monitors the market to ensure that all 
              information remains current and relevant.
            </p>
          </motion.div>
          
          <motion.div 
            className="values" 
            variants={fadeInUp}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Our Core Values</h2>
            <div className="values-grid staggered-animations">
              <motion.div 
                className="value"
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div 
                  className="value-icon"
                  animate={floatAnimation}
                  whileHover={{ 
                    scale: 1.1,
                    backgroundColor: "#D4AF37",
                    color: "#121F38"
                  }}
                >
                  <FaShieldAlt />
                </motion.div>
                <h3>Security</h3>
                <p>We prioritize the security of your funds and personal information</p>
              </motion.div>
              
              <motion.div 
                className="value"
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <motion.div 
                  className="value-icon"
                  animate={floatAnimation}
                  transition={{ delay: 0.2 }}
                  whileHover={{ 
                    scale: 1.1,
                    backgroundColor: "#D4AF37",
                    color: "#121F38"
                  }}
                >
                  <FaChartLine />
                </motion.div>
                <h3>Transparency</h3>
                <p>We provide clear, honest information about all listed brokers</p>
              </motion.div>
              
              <motion.div 
                className="value"
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.div 
                  className="value-icon"
                  animate={floatAnimation}
                  transition={{ delay: 0.4 }}
                  whileHover={{ 
                    scale: 1.1,
                    backgroundColor: "#D4AF37",
                    color: "#121F38"
                  }}
                >
                  <FaGraduationCap />
                </motion.div>
                <h3>Education</h3>
                <p>We empower traders with knowledge and resources for success</p>
              </motion.div>
              
              <motion.div 
                className="value"
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <motion.div 
                  className="value-icon"
                  animate={floatAnimation}
                  transition={{ delay: 0.6 }}
                  whileHover={{ 
                    scale: 1.1,
                    backgroundColor: "#D4AF37",
                    color: "#121F38"
                  }}
                >
                  <FaGlobeAmericas />
                </motion.div>
                <h3>Global Reach</h3>
                <p>We connect traders with brokers worldwide</p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;